import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Button, ConfigProvider, Tooltip } from "antd";
import {
  CloseOutlined,
  ThunderboltFilled,
} from "@ant-design/icons";
import SmartMapperPanel from "./SmartMapperPanel";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Site context — used to pick the right intake template and to scope
      the material catalog lookup. All four are optional; when missing,
      the panel falls back to the generic 001 template with no site. */
  siteId?: string | number;
  siteName?: string | null;
  segmentLevel?: string | null;
  segmentLabel?: string;
  templateId?: string;
}

/**
 * Full-screen overlay that hosts the Smart Mapper wizard. Portaled to
 * document.body so it sits above the Bulk Material Upload Modal (AntD
 * Modals default to z-index 1000; we use 9999 to clear them).
 *
 * Pattern mirrors the AI Insight Report fullscreen in AIInsightView.tsx
 * — Esc closes, header has an exit button, body scrolls.
 */
const SmartMapperFullscreen: React.FC<Props> = ({
  open,
  onClose,
  siteId,
  siteName,
  segmentLevel,
  segmentLabel,
  templateId,
}) => {
  const overlayRef = useRef<HTMLDivElement | null>(null);

  // Esc key closes the overlay.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock body scroll while the overlay is showing so the page underneath
  // doesn't shift when overlay content overflows.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    // ConfigProvider re-roots every AntD popup (Select dropdown, Tooltip,
    // DatePicker calendar etc.) inside this overlay so they inherit our
    // z-index 9999 stacking context. Otherwise they default to a
    // document.body portal at z-index ~1050 and are hidden behind us.
    <ConfigProvider
      getPopupContainer={(triggerNode) =>
        (triggerNode?.parentElement as HTMLElement) ||
        overlayRef.current ||
        document.body
      }
    >
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label="Smart AI Mapper"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "#f8fafc",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "12px 24px",
          background: "#fff",
          borderBottom: "1px solid #e6e8eb",
          boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            minWidth: 0,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background:
                "linear-gradient(135deg, #1e3a8a 0%, #4338ca 100%)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 4px 12px -4px rgba(30, 58, 138, 0.4)",
            }}
          >
            <ThunderboltFilled style={{ fontSize: 18 }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.2,
                letterSpacing: -0.2,
              }}
            >
              Smart AI Mapper
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: "#64748b",
                marginTop: 1,
              }}
            >
              Upload any Excel — AI maps your columns to the standard
              template, cleans the data, and returns a ready-to-use file.
            </div>
          </div>
        </div>
        <Tooltip title="Close (Esc)">
          <Button
            type="default"
            icon={<CloseOutlined />}
            onClick={onClose}
            style={{ borderRadius: 8, flexShrink: 0 }}
          >
            Close
          </Button>
        </Tooltip>
      </div>

      {/* Body — scrollable area centred on a max-width column */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "24px 32px 40px",
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            background: "#fff",
            border: "1px solid #e6e8eb",
            borderRadius: 14,
            padding: "20px 24px",
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
          }}
        >
          <SmartMapperPanel
            onClose={onClose}
            siteId={siteId}
            siteName={siteName}
            segmentLevel={segmentLevel}
            segmentLabel={segmentLabel}
            templateId={templateId}
          />
        </div>
      </div>
    </div>
    </ConfigProvider>,
    document.body
  );
};

export default SmartMapperFullscreen;
